/* ═════════════════════════ NotificationsBell ════════════════════════
   Bell icon + unread badge + dropdown panel. Polls /api/notifications
   every 45s and also whenever the dropdown is opened.

   Notifications come from two sources today:
     - team.join_request / team.join_approved  (auth.js)
     - feedback.comment / feedback.status      (feedback.js)

   Each record carries an optional `href` like "/?panel=feedback&id=xyz"
   which we use as a click-through. We navigate with window.location
   (full reload) so the target panel — even if it's outside the main App
   tree — can render correctly.
   ═══════════════════════════════════════════════════════════════════ */
import { useCallback, useEffect, useRef, useState } from "react";
import { Bell, X, MessageSquare, UserPlus, CheckCircle, AlertCircle } from "lucide-react";

const INK = "#000000";
const MUTED = "#6B7280";
const BORDER = "#E4E4E7";
const SOFT = "#F4F4F5";
const ACCENT = "#2C5766";

export default function NotificationsBell() {
  const [open, setOpen] = useState(false);
  const [list, setList] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch("/api/notifications", { credentials: "same-origin" });
      if (!r.ok) return;
      const data = await r.json();
      setList(Array.isArray(data.notifications) ? data.notifications : []);
      setUnread(Number(data.unread) || 0);
    } catch {}
    finally { setLoading(false); }
  }, []);

  // Initial load + background polling.
  useEffect(() => {
    load();
    const t = setInterval(load, 45_000);
    return () => clearInterval(t);
  }, [load]);

  // Refresh on open + mark-all-read when the panel first opens with unreads.
  useEffect(() => {
    if (!open) return;
    load();
  }, [open, load]);

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    function onDoc(e) { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); }
    function onKey(e) { if (e.key === "Escape") setOpen(false); }
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  async function markAllRead() {
    if (unread === 0) return;
    try {
      const r = await fetch("/api/notifications/mark-read", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({}),
      });
      if (r.ok) { setUnread(0); setList(list.map(n => ({ ...n, seen: true }))); }
    } catch {}
  }

  async function dismiss(id) {
    try {
      await fetch(`/api/notifications/${encodeURIComponent(id)}`, {
        method: "DELETE", credentials: "same-origin",
      });
    } catch {}
    setList(prev => prev.filter(n => n.id !== id));
  }

  function navigate(n) {
    if (n.href) window.location.assign(n.href);
    setOpen(false);
  }

  return (
    <div ref={wrapRef} style={{ position: "relative" }}>
      <button
        aria-label="Notifications"
        onClick={() => setOpen(o => !o)}
        style={{
          width: 36, height: 36, borderRadius: 10, border: "none",
          background: open ? SOFT : "transparent", cursor: "pointer",
          display: "flex", alignItems: "center", justifyContent: "center",
          position: "relative", transition: "background 0.12s",
        }}
        onMouseEnter={e => { if (!open) e.currentTarget.style.background = SOFT; }}
        onMouseLeave={e => { if (!open) e.currentTarget.style.background = "transparent"; }}
      >
        <Bell size={18} color={INK} strokeWidth={1.8} />
        {unread > 0 && (
          <span style={{
            position: "absolute", top: 6, right: 6,
            minWidth: 16, height: 16, padding: "0 4px",
            borderRadius: 8, background: ACCENT, color: "#fff",
            fontSize: 10, fontWeight: 700, lineHeight: "16px",
            textAlign: "center", boxSizing: "border-box",
            border: "2px solid #fff",
          }}>{unread > 99 ? "99+" : unread}</span>
        )}
      </button>

      {open && (
        <div style={{
          position: "absolute", right: 0, top: 44, zIndex: 100,
          width: 380, maxHeight: 520, display: "flex", flexDirection: "column",
          background: "#fff", border: `1px solid ${BORDER}`, borderRadius: 12,
          boxShadow: "0 16px 36px rgba(17,24,39,0.12), 0 4px 10px rgba(17,24,39,0.04)",
          overflow: "hidden", fontFamily: "Inter, sans-serif",
        }}>
          {/* Header */}
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            padding: "14px 16px", borderBottom: `1px solid ${BORDER}`,
          }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: INK }}>Notifications</div>
            <button
              onClick={markAllRead}
              disabled={unread === 0}
              style={{
                background: "none", border: "none",
                color: unread === 0 ? "#9CA3AF" : INK,
                fontSize: 12, fontWeight: 500,
                cursor: unread === 0 ? "default" : "pointer",
                padding: 0, textDecoration: unread === 0 ? "none" : "underline",
              }}
            >Mark all read</button>
          </div>

          {/* List */}
          <div style={{ overflowY: "auto", flex: 1 }}>
            {list.length === 0 ? (
              <div style={{ padding: "32px 20px", textAlign: "center", color: MUTED, fontSize: 13 }}>
                {loading ? "Loading..." : "You're all caught up."}
              </div>
            ) : list.map(n => (
              <NotificationRow key={n.id} n={n} onClick={() => navigate(n)} onDismiss={() => dismiss(n.id)} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function NotificationRow({ n, onClick, onDismiss }) {
  const Icon = iconFor(n.type);
  const color = colorFor(n.type);
  return (
    <div
      onClick={onClick}
      style={{
        display: "flex", gap: 12, padding: "12px 16px",
        borderBottom: `1px solid ${BORDER}`,
        background: n.seen ? "#fff" : "#EAF1F3",
        cursor: n.href ? "pointer" : "default",
        transition: "background 0.1s",
      }}
      onMouseEnter={e => { e.currentTarget.style.background = n.seen ? SOFT : "#D6E4E8"; }}
      onMouseLeave={e => { e.currentTarget.style.background = n.seen ? "#fff" : "#EAF1F3"; }}
    >
      <div style={{
        flexShrink: 0, width: 32, height: 32, borderRadius: 8,
        background: color.bg, display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <Icon size={16} color={color.fg} strokeWidth={1.8} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: INK, lineHeight: 1.35 }}>{n.title}</div>
        {n.body && <div style={{ fontSize: 12, color: MUTED, marginTop: 2, lineHeight: 1.45, wordBreak: "break-word" }}>{n.body}</div>}
        <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 4 }}>{timeAgo(n.createdAt)}</div>
      </div>
      <button
        onClick={e => { e.stopPropagation(); onDismiss(); }}
        aria-label="Dismiss"
        style={{
          background: "none", border: "none", cursor: "pointer",
          padding: 4, color: "#9CA3AF", alignSelf: "flex-start",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}
        onMouseEnter={e => { e.currentTarget.style.color = INK; }}
        onMouseLeave={e => { e.currentTarget.style.color = "#9CA3AF"; }}
      >
        <X size={14} strokeWidth={2} />
      </button>
    </div>
  );
}

function iconFor(type) {
  if (type === "team.join_request")  return UserPlus;
  if (type === "team.join_approved") return CheckCircle;
  if (type === "feedback.comment")   return MessageSquare;
  if (type === "feedback.status")    return CheckCircle;
  return AlertCircle;
}
function colorFor(type) {
  if (type === "team.join_request")  return { bg: "#EEF2FF", fg: "#4338CA" };
  if (type === "team.join_approved") return { bg: "#ECFDF5", fg: "#059669" };
  if (type === "feedback.comment")   return { bg: "#FEF3C7", fg: "#CC785C" };
  if (type === "feedback.status")    return { bg: "#FEF2F2", fg: ACCENT };
  return { bg: SOFT, fg: MUTED };
}
function timeAgo(ts) {
  if (!ts) return "";
  const d = Math.floor((Date.now() - ts) / 1000);
  if (d < 60) return "just now";
  if (d < 3600) return `${Math.floor(d / 60)}m ago`;
  if (d < 86400) return `${Math.floor(d / 3600)}h ago`;
  if (d < 604800) return `${Math.floor(d / 86400)}d ago`;
  return new Date(ts).toLocaleDateString();
}
